import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { MaterialPreviewRenderer, THREE } from '@easytwin/runtime';
import { classNames, EasyVIcon } from '../../index';

interface MaterialPreviewProps {
  material: THREE.Material;
}

export function MaterialPreview({ material }: MaterialPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const assetViewer = useRef<MaterialPreviewRenderer>();

  const isLineMaterial = material.type === 'LineMaterial';

  const [previewMode, setPreviewMode] = useState<'Sphere' | 'Box' | 'Ring'>('Sphere');

  useEffect(() => {
    if (!containerRef.current) return;

    const viewer = new MaterialPreviewRenderer(containerRef.current);

    assetViewer.current = viewer;

    viewer.startAnimation();

    viewer.setMaterial(material);

    return () => {
      if (assetViewer.current) {
        assetViewer.current.dispose();
      }
    };
  }, [material]);

  return (
    <div className='mt-2 h-[100px] flex gap-x-2 w-full'>
      <div className='flex-1 rounded-sm h-25 w-0 material-preview-container' ref={containerRef} />
      <div className='flex flex-col w-6'>
        {isLineMaterial ? (
          <div>
            <Gizmo
              className='rounded-t'
              icon={<EasyVIcon.BatchModelLinesOutlined />}
              highlight={true}
            />
          </div>
        ) : (
          <div>
            <Gizmo
              className='rounded-t'
              icon={<EasyVIcon.SphereOutlined />}
              highlight={previewMode === 'Sphere'}
              onClick={() => {
                setPreviewMode('Sphere');
                assetViewer.current?.setPreviewMode('sphere');
              }}
            />
            <Gizmo
              className='rounded-b'
              icon={<EasyVIcon.ModelOutlined />}
              highlight={previewMode === 'Box'}
              onClick={() => {
                setPreviewMode('Box');
                assetViewer.current?.setPreviewMode('cube');
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface GizmoProps {
  className?: string;
  icon: ReactNode;
  interactive?: boolean;
  highlight?: boolean;
  onClick?: () => void;
}

export function Gizmo(props: GizmoProps) {
  const { className, icon, highlight, interactive = true, onClick } = props;
  return (
    <div
      className={classNames(
        'relative p-1.5 border rounded-sm cursor-pointer h-6 w-6 flex items-center justify-center',
        interactive
          ? highlight
            ? 'border-primary text-white hover:border-primary hover:text-white'
            : 'border-borderColorMain bg-bgColorFill1 text-textColorBase hover:border-primary hover:text-white'
          : 'bg-textColorDisabled border-textColorDisabled text-textColorBase',
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (interactive) {
          onClick?.();
        }
      }}
    >
      {icon}
    </div>
  );
}
